import { useEffect, useMemo, useState } from "react";
import {
  clearJicSession,
  exchangeJicCodeFromUrl,
  fetchJicHandle,
  getJicUser,
  loginJicEmbedded,
  loginJic,
  logoutJic,
  onJicChange,
  openJicCabin,
  syncJicAfterCabinIfNeeded,
  syncSsoSession,
  type JicUser,
} from "../lib/jic";
import { getInjectedWalletAddress } from "../lib/injectedWallet";

const baseButton =
  "h-10 rounded-full border-0 px-4 text-xs font-extrabold shadow-[0_4px_18px_rgba(0,0,0,0.35)] transition active:scale-95";

type EthereumProvider = {
  isTokenPocket?: boolean;
  providers?: EthereumProvider[];
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
  on?: (event: "accountsChanged", listener: (accounts: string[]) => void) => void;
  removeListener?: (
    event: "accountsChanged",
    listener: (accounts: string[]) => void
  ) => void;
};

declare global {
  interface Window {
    ethereum?: EthereumProvider;
    tokenpocket?: {
      ethereum?: EthereumProvider;
    };
  }
}

function getTokenPocketProvider() {
  if (typeof window === "undefined") return null;
  const ethereum = window.ethereum;
  const provider = ethereum?.providers?.find((item) => item.isTokenPocket);
  if (provider) return provider;
  if (ethereum?.isTokenPocket) return ethereum;
  if (window.tokenpocket?.ethereum) return window.tokenpocket.ethereum;
  return null;
}

function isTokenPocketBrowser() {
  if (typeof window === "undefined") return false;
  return Boolean(getTokenPocketProvider()) || /TokenPocket/i.test(navigator.userAgent);
}


export default function JicFloatingWidget({ icon = "👽", gold = false }: { icon?: string; gold?: boolean } = {}) {
  const [user, setUser] = useState<JicUser | null>(() => getJicUser());
  const [handle, setHandle] = useState<string | null>(null);
  const [isTokenPocket, setIsTokenPocket] = useState(false);
  const [tpConnecting, setTpConnecting] = useState(false);
  const [tpError, setTpError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    exchangeJicCodeFromUrl().then((exchanged) => {
      if (!live || exchanged) return;
      if (syncJicAfterCabinIfNeeded()) return;
      void syncSsoSession();   // 其他 jic.io 子域名已登录 → 静默同步进本站
    });

    const syncAfterReturn = () => {
      syncJicAfterCabinIfNeeded();
    };
    window.addEventListener("pageshow", syncAfterReturn);
    window.addEventListener("focus", syncAfterReturn);

    return () => {
      live = false;
      window.removeEventListener("pageshow", syncAfterReturn);
      window.removeEventListener("focus", syncAfterReturn);
    };
  }, []);

  useEffect(() => onJicChange(setUser), []);

  useEffect(() => {
    setIsTokenPocket(isTokenPocketBrowser());
  }, []);

  useEffect(() => {
    const provider = getTokenPocketProvider();
    if (!provider?.on) return;

    const onAccountsChanged = (accounts: string[]) => {
      const address = accounts[0];
      if (address) {
        loginJicEmbedded(address);
      } else {
        clearJicSession();
      }
    };

    provider.on("accountsChanged", onAccountsChanged);
    return () => provider.removeListener?.("accountsChanged", onAccountsChanged);
  }, []);

  useEffect(() => {
    let live = true;
    setHandle(null);
    if (!user?.sub) return;
    fetchJicHandle(user.sub).then((name) => {
      if (live) setHandle(name);
    });
    return () => {
      live = false;
    };
  }, [user?.sub]);

  const displayName = useMemo(() => {
    if (!user?.sub) return "";
    const name = handle || `${user.sub.slice(0, 6)}...${user.sub.slice(-4)}`;
    return `${handle ? "@" : ""}${name}${user.type === "agent" ? " 🤖" : ""}`;
  }, [handle, user?.sub, user?.type]);

  const handleLogout = async () => {
    if (user?.source === "embedded") {
      clearJicSession();
      return;
    }

    logoutJic();
  };

  const handleTokenPocketLogin = async () => {
    setTpError(null);
    const provider = getTokenPocketProvider();
    if (!provider) {
      setTpError("请在 TokenPocket 内置浏览器打开");
      return;
    }

    setTpConnecting(true);
    try {
      const address = await getInjectedWalletAddress();
      if (!address) throw new Error("TokenPocket 没有返回钱包地址");
      loginJicEmbedded(address);
    } catch (error) {
      setTpError(error instanceof Error ? error.message : "TokenPocket 登录失败");
    } finally {
      setTpConnecting(false);
    }
  };

  const handleAlienClick = () => {
    if (user) {
      openJicCabin();
      return;
    }
    if (isTokenPocket) {
      handleTokenPocketLogin();
      return;
    }
    loginJic({ clearLocal: true });
  };

  return (
    <div className="fixed bottom-4 right-4 z-[99999] flex max-w-[calc(100vw-2rem)] flex-col items-end gap-2 font-sans">
      <div className="flex max-w-full items-center gap-2">
        <button
          type="button"
          className={`flex h-11 w-11 items-center justify-center rounded-full border text-xl transition hover:-translate-y-0.5 hover:scale-105 active:scale-95 ${gold ? "border-amber-300/60 bg-[#221604] shadow-[0_0_16px_rgba(255,196,90,0.45)]" : "border-cyan-300/60 bg-[#081428] shadow-[0_0_16px_rgba(63,224,255,0.4)]"}`}
          title={user ? "回到 JIC 指挥舱" : "JIC 登录 / 回到指挥舱"}
          aria-label={user ? "Command cabin" : "JIC login"}
          disabled={tpConnecting}
          onClick={handleAlienClick}
        >
          {tpConnecting ? "…" : icon}
        </button>
        {user && (
          <button
            type="button"
            className={`${baseButton} max-w-[calc(100vw-5rem)] truncate bg-[rgba(12,23,48,0.92)] text-slate-200 backdrop-blur`}
            title="退出当前站点上的 JIC 登录"
            onClick={handleLogout}
          >
            {displayName} · 退出
          </button>
        )}
      </div>
      {tpError && (
        <div className="max-w-[calc(100vw-2rem)] rounded-full bg-black/75 px-3 py-1 text-[11px] font-bold text-rose-100 shadow-[0_4px_18px_rgba(0,0,0,0.35)] backdrop-blur">
          {tpError}
        </div>
      )}
    </div>
  );
}
